import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Scissors, Plus, Clock, User, Phone, Calendar, Check, X, 
  RefreshCw, Search, ChevronRight, AlertCircle, Loader2
} from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { format } from 'date-fns';
import { localToday } from '@/lib/dateRange';

const STATUS_CONFIG = {
  booked: { bg: 'bg-info/20 dark:bg-blue-900/30', text: 'text-info dark:text-blue-400', label: 'Booked', icon: Calendar },
  in_progress: { bg: 'bg-warning/20 dark:bg-amber-900/30', text: 'text-warning dark:text-amber-400', label: 'In Progress', icon: Clock },
  completed: { bg: 'bg-success/20 dark:bg-emerald-900/30', text: 'text-success dark:text-emerald-400', label: 'Completed', icon: Check },
  cancelled: { bg: 'bg-error/20 dark:bg-red-900/30', text: 'text-error dark:text-red-400', label: 'Cancelled', icon: X },
  no_show: { bg: 'bg-muted dark:bg-muted', text: 'text-muted-foreground dark:text-muted-foreground', label: 'No Show', icon: AlertCircle },
};

const TIME_SLOTS = [
  '08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30',
  '12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30',
  '16:00','16:30','17:00','17:30','18:00','18:30','19:00','19:30',
  '20:00','20:30','21:00','21:30','22:00',
];

const DURATIONS = [15, 30, 45, 60, 90, 120];

const emptyForm = {
  customer_name: '', customer_phone: '', service_name: '', stylist_name: '',
  date: localToday(), time: '10:00',
  duration_minutes: 30, price: '', notes: '', status: 'booked', branch_id: '',
};

function AppointmentCard({ appt, onEdit, onStatusChange }) {
  const cfg = STATUS_CONFIG[appt.status] || STATUS_CONFIG.booked;
  const Icon = cfg.icon;
  return (
    <div className={`border rounded-xl p-4 transition-all hover:shadow-md ${cfg.bg}`} data-testid={`appt-${appt.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm font-outfit dark:text-white truncate">{appt.customer_name || 'Walk-in'}</span>
            <Badge className={`${cfg.bg} ${cfg.text} text-[10px] border-0`}><Icon size={10} className="mr-0.5" />{cfg.label}</Badge>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1"><Scissors size={11} />{appt.service_name || 'Service'}</span>
            {appt.stylist_name && <span className="flex items-center gap-1"><User size={11} />{appt.stylist_name}</span>}
            <span className="flex items-center gap-1"><Clock size={11} />{appt.time} ({appt.duration_minutes}min)</span>
            {appt.customer_phone && <span className="flex items-center gap-1"><Phone size={11} />{appt.customer_phone}</span>}
          </div>
          {appt.price > 0 && <p className="text-xs font-semibold text-success mt-1 inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {Number(appt.price).toLocaleString()}</p>}
          {appt.notes && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{appt.notes}</p>}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => onEdit(appt)} data-testid={`edit-appt-${appt.id}`}>Edit</Button>
          {appt.status === 'booked' && (
            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-warning" onClick={() => onStatusChange(appt.id, 'in_progress')} data-testid={`start-appt-${appt.id}`}>Start</Button>
          )}
          {appt.status === 'in_progress' && (
            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-success" onClick={() => onStatusChange(appt.id, 'completed')} data-testid={`complete-appt-${appt.id}`}>Done</Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SalonAppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [filterDate, setFilterDate] = useState(localToday());
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      const [apptRes, brRes] = await Promise.all([
        api.get('/pos/appointments', { params: { date: filterDate || undefined, status: filterStatus !== 'all' ? filterStatus : undefined } }),
        api.get('/branches'),
      ]);
      setAppointments(apptRes.data);
      setBranches(brRes.data);
    } catch { toast.error('Failed to load appointments'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filterDate, filterStatus]);

  const openNew = () => {
    setEditingAppt(null);
    setForm({ ...emptyForm, branch_id: branches[0]?.id || '' });
    setShowDialog(true);
  };
  const openEdit = (appt) => {
    setEditingAppt(appt);
    setForm({
      customer_name: appt.customer_name || '', customer_phone: appt.customer_phone || '',
      service_name: appt.service_name || '', stylist_name: appt.stylist_name || '',
      date: appt.date || '', time: appt.time || '10:00',
      duration_minutes: appt.duration_minutes || 30, price: appt.price || '',
      notes: appt.notes || '', status: appt.status || 'booked', branch_id: appt.branch_id || '',
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.customer_name && !form.service_name) { toast.error('Customer name or service required'); return; }
    try {
      const payload = { ...form, price: form.price ? parseFloat(form.price) : 0, duration_minutes: parseInt(form.duration_minutes) };
      if (editingAppt) {
        await api.put(`/pos/appointments/${editingAppt.id}`, payload);
        toast.success('Appointment updated');
      } else {
        await api.post('/pos/appointments', payload);
        toast.success('Appointment booked');
      }
      setShowDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/pos/appointments/${id}`, { status });
      toast.success(`Appointment ${status.replace('_', ' ')}`);
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      await api.delete(`/pos/appointments/${id}`);
      toast.success('Appointment deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = appointments.filter(a => {
    if (filterBranch !== 'all' && a.branch_id !== filterBranch) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.customer_name || '').toLowerCase().includes(q) ||
        (a.service_name || '').toLowerCase().includes(q) ||
        (a.stylist_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const stats = {
    total: filtered.length,
    booked: filtered.filter(a => a.status === 'booked').length,
    in_progress: filtered.filter(a => a.status === 'in_progress').length,
    completed: filtered.filter(a => a.status === 'completed').length,
  };

  const timeGroups = {};
  filtered.sort((a, b) => (a.time || '').localeCompare(b.time || '')).forEach(a => {
    const hour = (a.time || '00:00').split(':')[0] + ':00';
    if (!timeGroups[hour]) timeGroups[hour] = [];
    timeGroups[hour].push(a);
  });

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64 text-muted-foreground"><Loader2 className="animate-spin mr-2" size={20} />Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="salon-appointments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-200/50">
              <Scissors size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Salon Appointments</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Manage salon & spa bookings</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" size="sm" className="rounded-xl"><RefreshCw size={14} /></Button>
            <Button onClick={openNew} className="rounded-xl" data-testid="new-appointment-btn"><Plus size={14} className="mr-1" />Book Appointment</Button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card data-testid="stat-total"><CardContent className="pt-4 pb-3"><div className="text-2xl font-bold font-outfit">{stats.total}</div><div className="text-xs text-muted-foreground">Total</div></CardContent></Card>
          <Card className="border-info/30" data-testid="stat-booked"><CardContent className="pt-4 pb-3"><div className="text-2xl font-bold font-outfit text-info">{stats.booked}</div><div className="text-xs text-muted-foreground">Booked</div></CardContent></Card>
          <Card className="border-warning/30" data-testid="stat-in-progress"><CardContent className="pt-4 pb-3"><div className="text-2xl font-bold font-outfit text-warning">{stats.in_progress}</div><div className="text-xs text-muted-foreground">In Progress</div></CardContent></Card>
          <Card className="border-success/30" data-testid="stat-completed"><CardContent className="pt-4 pb-3"><div className="text-2xl font-bold font-outfit text-success">{stats.completed}</div><div className="text-xs text-muted-foreground">Completed</div></CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <Input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="w-40 h-9 text-sm rounded-xl" data-testid="filter-date" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32 h-9 text-sm rounded-xl" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {branches.length > 1 && (
            <Select value={filterBranch} onValueChange={setFilterBranch}>
              <SelectTrigger className="w-36 h-9 text-sm rounded-xl" data-testid="filter-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="relative flex-1 min-w-[160px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customer, service..." className="pl-9 h-9 text-sm rounded-xl" data-testid="search-input" />
          </div>
        </div>

        {/* Timeline View */}
        {filtered.length === 0 ? (
          <Card><CardContent className="py-16 text-center">
            <Scissors size={40} className="mx-auto mb-3 opacity-20" />
            <p className="text-muted-foreground text-sm">No appointments for this date</p>
            <Button onClick={openNew} variant="outline" className="mt-3 rounded-xl" size="sm"><Plus size={14} className="mr-1" />Book First Appointment</Button>
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {Object.entries(timeGroups).map(([hour, appts]) => (
              <div key={hour}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-xs font-semibold text-muted-foreground bg-muted dark:bg-muted px-2.5 py-1 rounded-lg">{hour}</div>
                  <div className="flex-1 h-px bg-muted dark:bg-stone-700" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pl-4">
                  {appts.map(a => (
                    <AppointmentCard key={a.id} appt={a} onEdit={openEdit} onStatusChange={handleStatusChange} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Book/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md" data-testid="appointment-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">{editingAppt ? 'Edit Appointment' : 'Book Appointment'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Customer Name *</Label>
                  <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer name" data-testid="form-customer-name" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} placeholder="05xxxxxxxx" data-testid="form-phone" />
                </div>
                <div>
                  <Label>Branch</Label>
                  <Select value={form.branch_id} onValueChange={v => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger data-testid="form-branch"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Service *</Label>
                  <Input value={form.service_name} onChange={e => setForm({ ...form, service_name: e.target.value })} placeholder="Haircut, Facial, etc." data-testid="form-service" />
                </div>
                <div>
                  <Label>Stylist</Label>
                  <Input value={form.stylist_name} onChange={e => setForm({ ...form, stylist_name: e.target.value })} placeholder="Stylist name" data-testid="form-stylist" />
                </div>
                <div>
                  <Label>Price (SR)</Label>
                  <Input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="0.00" data-testid="form-price" />
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required data-testid="form-date" />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Select value={form.time} onValueChange={v => setForm({ ...form, time: v })}>
                    <SelectTrigger data-testid="form-time"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duration</Label>
                  <Select value={String(form.duration_minutes)} onValueChange={v => setForm({ ...form, duration_minutes: parseInt(v) })}>
                    <SelectTrigger data-testid="form-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>{DURATIONS.map(d => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {editingAppt && (
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="booked">Booked</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="no_show">No Show</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Special requests..." data-testid="form-notes" />
              </div>
              <DialogFooter className="flex gap-2">
                {editingAppt && (
                  <Button type="button" variant="destructive" size="sm" onClick={() => { handleDelete(editingAppt.id); setShowDialog(false); }} data-testid="delete-appt-btn">Delete</Button>
                )}
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-appt-btn">{editingAppt ? 'Update' : 'Book'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
